import React from "react";

import { dupInsuranceProductTypes, dupPaymentPeriod } from "../../../Enums/enumValues";
import { DefaultCoverage, PersonCoverage } from "../../../interfaces/dup";
import { Enum, UseOfName, YesOrNo } from "../../../interfaces/enum";
import { PaymentTerm } from "../../../interfaces/paymentTerm";
import { SectionWrapper } from "../../../layouts/sectionWrapper/SectionWrapper";
import { formatCurrency } from "../../../lib/currencyUtil";
import dateUtil from "../../../lib/dateUtil";
import { getFullName } from "../../../lib/nameUtil";
import { Premium, PremiumTerms } from "../../../providers/dupFlow/DupFlow.interfaces";
import { currencyThousandFormat } from "../../InputFields/sanitizer/currencyFormat";
import { ListWithHeader } from "../../lists/listWithHeader/ListWithHeader";
import { PersonalDetails } from "../../personalDetails/PersonalDetails";
import styles from "./DupQuotationOverview.module.scss";

interface InsuredData {
  personId: string;
  isDefault: boolean;
  isChild: boolean;
  dateOfBirth: string;
  street: string;
  houseNumber: number | null;
  city: string;
  postcode: string;
  emailAddress: string;
  mobileNumber: string;
  salutation: string;
  initials: string;
  firstName: string;
  lastName: string;
  location: string;
  inserts: string;
  hasPartner: boolean;
  partnerInserts: string;
  partnerLastName: string;
  useOfName: UseOfName;
  telephoneNumber: string;
  childAsAdultPremium: YesOrNo;
  personCoverage?: PersonCoverage;
}

interface OldPremiumTerms {
  terms: Premium[];
}

interface Props {
  paymentTerm: PaymentTerm;
  insuredPersons: Partial<InsuredData>[];
  defaultCoverage: Partial<DefaultCoverage>;
  premium: PremiumTerms | OldPremiumTerms | null;
  dictionary: Record<string, string>;
  policyCosts: string;
  showDisclaimer?: true;
}

export const DupQuotationOverview = ({
  insuredPersons,
  defaultCoverage,
  premium,
  paymentTerm,
  policyCosts,
  dictionary = {},
  showDisclaimer,
}: Props) => {
  const isPurchaseMoneyProduct = defaultCoverage.insuranceType === "PurchaseMoneyProduct";

  const getTotalPremium = () => {
    if (!premium) {
      return undefined;
    }

    if ("totalPremiumPerTerms" in premium) {
      return premium.totalPremiumPerTerms.find((t) => t.paymentTerm === paymentTerm)?.premium;
    }

    if ("terms" in premium) {
      return premium.terms.find((t) => t.paymentTerm === paymentTerm)?.premium;
    }

    return undefined;
  };

  const totalPremium = getTotalPremium();

  const isMoneyInsurance = (coverageType: string) => coverageType === "GeldVol" || coverageType === "GeldKind";

  const getInsuranceType = (coverageType: string) =>
    dictionary[`coverCode.${coverageType.toLowerCase()}`] ?? coverageType;

  const getPaymentTerm = () => dictionary[`paymentTerm.${paymentTerm.toLowerCase()}`] ?? paymentTerm;

  const getPremiumPerCoverageType = (insured: Partial<InsuredData>) => {
    return premium && "premiumPerPersons" in premium
      ? premium.premiumPerPersons?.find((p) => p.personId === insured.personId)?.premiumsPerCoverageType
      : undefined;
  };

  const getCoverageType = (i: Partial<InsuredData>) => {
    return i.isChild && !isPurchaseMoneyProduct ? (
      <span className={styles.dupQuotationOverview__childAsAdultPremium}>
        {i.childAsAdultPremium === "Yes" && "(Volwassen premie)"}
        {i.childAsAdultPremium === "No" && "(Kind premie)"}
      </span>
    ) : (
      <></>
    );
  };

  const getFormattedInsuredAmount = (
    coverageType: string,
    i: Partial<InsuredData>,
    insuredAmount: number | undefined
  ) => (
    <span
      key={`insuredAmount_${coverageType}_${i.personId}`}
      style={{ opacity: isMoneyInsurance(coverageType) ? 1 : 0 }}
    >
      {isMoneyInsurance(coverageType) && insuredAmount ? currencyThousandFormat(insuredAmount) : "-"}
    </span>
  );

  const getFormattedPremium = (paymentTermPremium: Premium | undefined) =>
    paymentTermPremium ? formatCurrency(paymentTermPremium.premium) : " ";

  const insuredPersonsData = insuredPersons.map((i) => {
    const insuranceType = i.isDefault ? defaultCoverage.insuranceType : i.personCoverage?.insuranceType;
    const insuranceTypeReference = (dupInsuranceProductTypes() as Enum[]).find((dipt) => dipt.value === insuranceType);
    const insuranceTypeLabel = insuranceTypeReference ? insuranceTypeReference.label : "";
    const insuredAmount = i.isDefault ? defaultCoverage.insuredAmount : i.personCoverage?.insuredAmount;

    const premiumsPerCoverageType = getPremiumPerCoverageType(i);

    const coverageType = getCoverageType(i);

    return {
      insured: (
        <>
          <span>{getFullName(i)}</span>
          {i.dateOfBirth && <span>{dateUtil.dutchDateToDateWithWrittenMonth(i.dateOfBirth)}</span>}
        </>
      ),
      insuranceType: premiumsPerCoverageType
        ? premiumsPerCoverageType.map((ip) => (
            <span key={`insuranceType_${ip.coverageType}_${i.personId}`}>{getInsuranceType(ip.coverageType)}</span>
          ))
        : insuranceTypeLabel,
      insuredAmount: premiumsPerCoverageType
        ? premiumsPerCoverageType.map((ip) => getFormattedInsuredAmount(ip.coverageType, i, insuredAmount))
        : insuredAmount
        ? currencyThousandFormat(insuredAmount)
        : "-",
      premium: premiumsPerCoverageType
        ? premiumsPerCoverageType
            .map((ip) => {
              const paymentTermPremium = ip.premiumsPerTerms.find((t) => t.paymentTerm === paymentTerm);

              return (
                <span key={`premium_${ip.coverageType}_${i.personId}`}>{getFormattedPremium(paymentTermPremium)}</span>
              );
            })
            .concat([coverageType])
        : "",
    };
  });

  const convertedPaymentPeriod = (dupPaymentPeriod() as Enum[]).find(
    (x) => x.value === defaultCoverage.paymentPeriod
  )?.label;

  const insurer = insuredPersons.find((insuredPerson) => insuredPerson.isDefault);

  if (!insurer) {
    return null;
  }

  return (
    <SectionWrapper
      headerTitle="Uitvaartverzekering"
      infoBlocks={[
        {
          label: "Product",
          content: "DELA UitvaartPlan",
        },
        {
          label: "Totale premie",
          content: totalPremium ? `${formatCurrency(totalPremium)} ${getPaymentTerm().toLowerCase()}` : "-",
        },
        {
          label: "Verzekeringsnemer",
          content: getFullName(insurer),
        },
      ]}
    >
      <h4 className={styles.dupQuotationOverview__sectionHeader}>Verzekering</h4>
      {isPurchaseMoneyProduct ? (
        <ListWithHeader
          columns={[
            {
              accessor: "effectiveDate",
              Header: "Ingangsdatum",
              flex: 2.5,
            },
            {
              accessor: "policyCosts",
              Header: "Betaaltermijn",
              flex: 2,
            },
          ]}
          data={[
            {
              effectiveDate: defaultCoverage.effectiveDate
                ? dateUtil.dutchDateToDateWithWrittenMonth(defaultCoverage.effectiveDate)
                : "-",
              policyCosts: formatCurrency(3.5),
            },
          ]}
          hideLastBorder
        />
      ) : (
        <ListWithHeader
          columns={[
            {
              accessor: "effectiveDate",
              Header: "Ingangsdatum",
              flex: 1.5,
            },
            {
              accessor: "paymentTerm",
              Header: "Betaaltermijn",
            },
            {
              accessor: "paymentPeriod",
              Header: "Looptijd premie betaling",
            },
            {
              accessor: "policyCosts",
              Header: "Eenmalige poliskosten",
            },
          ]}
          data={[
            {
              effectiveDate: defaultCoverage.effectiveDate
                ? dateUtil.dutchDateToDateWithWrittenMonth(defaultCoverage.effectiveDate)
                : "-",
              paymentTerm: getPaymentTerm(),
              paymentPeriod: convertedPaymentPeriod,
              policyCosts,
            },
          ]}
          hideLastBorder
        />
      )}

      <h4 className={styles.dupQuotationOverview__sectionHeader}>Dekking</h4>
      <ListWithHeader
        columns={[
          {
            accessor: "insured",
            Header: insuredPersonsData.length > 1 ? "Verzekerden" : "Verzekerde",
            flex: 1.5,
          },
          { accessor: "insuranceType", Header: "Type uitvaartverzekering" },
          {
            accessor: "insuredAmount",
            Header: isPurchaseMoneyProduct ? "Verzekerd bedrag" : "(Aanvullend) verzekerd bedrag",
          },
          {
            accessor: "premium",
            Header: isPurchaseMoneyProduct ? "Koopsom" : "Premie",
          },
        ]}
        data={insuredPersonsData}
      />

      <div className={styles.dupQuotationOverview__premiumWrapper}>
        <div className={styles.dupQuotationOverview__premiumContainerLeft} />
        <div className={styles.dupQuotationOverview__premiumContainerRight}>
          <div>
            <p>
              {isPurchaseMoneyProduct ? "Totaal" : "Totale premie "}
              {isPurchaseMoneyProduct ? "" : getPaymentTerm().toLowerCase()}:
            </p>
          </div>
          <div>
            <p>{totalPremium ? formatCurrency(totalPremium) : "-"}</p>
          </div>
        </div>
      </div>

      {showDisclaimer && (
        <p className={styles.dupQuotationOverview__disclaimer_text}>
          Aan deze offerte kun je geen rechten ontlenen. We berekenen je definitieve premie als we de ingevulde
          gezondheidsvragen hebben ontvangen en de definitieve ingangsdatum bekend is.
        </p>
      )}
      <h4 className={styles.dupQuotationOverview__sectionHeader}>Contactgegevens</h4>
      <PersonalDetails details={insurer} />
    </SectionWrapper>
  );
};
